// DeckEditorPage: Deck-Editor mit verschiedenen Ansichten (Stacks, Rows, Table, Compact)
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowDown } from 'lucide-react';
import { json, apiClient } from '../../api/apiClient';
import { errorMessage } from '../../api/errorMessage';
import StacksView from './views/StacksView';
import RowsView from './views/RowsView';
import TableView from './views/TableView';
import CompactView from './views/CompactView';
import { TokenSection, countUniqueTokens } from './components/TokenSection';
import EditorFilterStrip from './components/EditorFilterStrip';
import FloatingAddDock from './components/FloatingAddDock';
import EditorActionBar from './components/EditorActionBar';
import ExternalDropBar from './components/ExternalDropBar';
import useExternalCardDrop, { EXTERNAL_DROP_AUTO } from './hooks/useExternalCardDrop';
import CatalogPanel from './catalog/CatalogPanel';
import { useDropSuggestionFromCache } from './catalog/useDeckSuggestions';
import CommandBridge from './components/bridge/CommandBridge';
import CommanderStack from './components/bridge/CommanderStack';
import BridgeSection from './components/bridge/BridgeSection';
import ManaCurve from './components/ManaCurve';
import HypergeometricStats from './components/HypergeometricStats';
import ManaBaseSection from './components/bridge/ManaBaseSection';
import WantlistRow from './components/bridge/WantlistRow';
import SuggestionSection from './components/bridge/SuggestionSection';
import ProgressBar from '../../ui/ProgressBar';
import { formatCategoryName } from '../../ui/formatCategoryName';
import InfoHint from '../../ui/InfoHint';
import DeckExportModal from './components/DeckExportModal';
import CardDetail from '../../components/CardDetail';
import { useUpdateCardMetadata } from '../../hooks/useWantlistData';
import AddCategoryDialog from './components/AddCategoryDialog';
import useRightInset from './hooks/useRightInset';
import UiCardImage from '../../ui/CardImage';
import { useDeckCardActions, AUTOCOMPLETE_DEBOUNCE_MS, mergeCategoryIntoDeckCache } from './hooks/useDeckCardActions';
import { DROP_ANIMATION_DURATION_MS } from './hooks/useDeckDragHandlers';
import { isCommanderDrop, parseCatalogCardId } from './utils/dragCardUtils';
import { deckNumericId } from './utils/deckIds';
import { commanderColorFilter } from './utils/commanderColorFilter';
import { notifyAddResult } from './utils/notifyAddResult';
import { useDeckSyntaxSearch } from './hooks/useDeckSyntaxSearch';
import { useEditorPreferences, VIEW_MODES, MIN_COLUMNS, MAX_COLUMNS } from '../../hooks/useEditorPreferences';
import { useDeckStats, SORT_OPTIONS } from './hooks/useDeckFiltering';
import { useAuth } from '../../context/AuthContext';
import { GROUPING_STRATEGIES, getStrategy } from './utils/groupingStrategies';
import { useDeckLabels } from './hooks/useDeckLabels';
import { useDeckUndo } from './hooks/useDeckUndo';
import { useDockSnap } from './hooks/useDockSnap';
import { MOBILE_MEDIA_QUERY } from '../../ui/breakpoints';
import NewLabelModal from './components/NewLabelModal';
import { useToast } from '../../ui/Toast';
import Page from '../../ui/Page';
import { computeDeckTotals } from './utils/formatPrice';
import './DeckEditor.css';
import './EditorChrome.css';
import '../../components/CardViews.css';

const SCRYFALL_ID_REGEX = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

function parseDroppedCardReference(urlString) {
  if (!urlString || typeof urlString !== 'string') return null;
  try {
    const parsed = new URL(urlString.trim());
    const host = parsed.hostname.toLowerCase();
    const isScryfall = host.includes('scryfall.com');
    const isEdhrec = host.includes('edhrec.com');
    const isScryfallCardImage = host.includes('cards.scryfall.io');
    if (!isScryfall && !isEdhrec && !isScryfallCardImage) return null;

    const idMatch = parsed.pathname.match(SCRYFALL_ID_REGEX);
    if (idMatch?.[1]) {
      return { scryfallId: idMatch[1].toLowerCase() };
    }

    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;

    const rawSlug = decodeURIComponent(segments[segments.length - 1] || '').toLowerCase();
    if (!rawSlug || rawSlug === 'card' || rawSlug === 'commanders') return null;

    const cleaned = rawSlug
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned) return null;
    return { cardName: cleaned };
  } catch {
    return null;
  }
}

function extractCardReferenceFromDropData(transfer) {
  if (!transfer) return null;

  const directUrl = transfer.getData('text/uri-list');
  const fromDirect = parseDroppedCardReference(directUrl);
  if (fromDirect) return fromDirect;

  const plainText = transfer.getData('text/plain');
  const fromPlainDirect = parseDroppedCardReference(plainText);
  if (fromPlainDirect) return fromPlainDirect;

  const urlMatch = plainText?.match(/https?:\/\/[^\s"'<>]+/i)?.[0];
  const fromPlainMatch = parseDroppedCardReference(urlMatch || '');
  if (fromPlainMatch) return fromPlainMatch;

  const html = transfer.getData('text/html');
  const htmlUrl = html?.match(/https?:\/\/[^\s"'<>]+/i)?.[0];
  const fromHtml = parseDroppedCardReference(htmlUrl || '');
  if (fromHtml) return fromHtml;

  return null;
}

export default function DeckEditorPage({ selectedDeck, isOwnProfile, onDeckUpdate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAdmin } = useAuth();
  const { push: pushToast } = useToast();
  const {
    columnCount, viewMode, setColumnCount, setViewMode,
    bridgeSide, setBridgeSide, catalogWidth, setCatalogWidth, catalogTab, setCatalogTab,
  } = useEditorPreferences(currentUser?.id);
  // Offen/zu und Stufe 2 sind Sitzungs-Zustand wie das Einklappen der Brücke: der Editor
  // öffnet mit zugeklapptem Katalog, gespeichert werden nur Breite und Reiter.
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogStageTwo, setCatalogStageTwo] = useState(false);
  const [playtestLoading, setPlaytestLoading] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedCmc, setHighlightedCmc] = useState(null);
  // Die Brücke soll ohne Scrollbalken aufgehen — die Kurve ist die höchste Sektion und startet zu.
  const [curveOpen, setCurveOpen] = useState(false);
  const [oddsOpen, setOddsOpen] = useState(false);
  const [manaBaseOpen, setManaBaseOpen] = useState(false);
  // Startet offen: die Sektion IST das Schaufenster der Vorschlags-Maschine und zeigt
  // zugeklappt nur einen Kartennamen.
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);
  const [groupingMode, setGroupingMode] = useState('categories');
  const strategy = getStrategy(groupingMode);
  const [highlightedNewCardId, setHighlightedNewCardId] = useState(null);
  const [deckNameDraft, setDeckNameDraft] = useState('');
  const [isEditingDeckName, setIsEditingDeckName] = useState(false);
  const deckNameInputRef = useRef(null);

  // Ziel der Tokens-Sprungzeile in der Kommandobrücke
  const tokenSectionRef = useRef(null);

  // Controls-Bar Höhe messen für sticky Category-Headers. getBoundingClientRect().height
  // statt offsetHeight: offsetHeight rundet auf ganze Pixel, der Kategorie-Kopf klebt
  // subpixelgenau darunter — die Rundungsdifferenz wäre ein Schlitz, durch den der
  // Inhalt scrollt. Kein Zuschlag: den Abstand zum Kopf liefert das padding-bottom
  // von .de-controls, das in der Messung schon enthalten ist.
  const controlsBarRef = useRef(null);
  const playmatRef = useRef(null);
  const dockRightInset = useRightInset(playmatRef);
  const [controlsBarHeight, setControlsBarHeight] = useState(0);
  useEffect(() => {
    const el = controlsBarRef.current;
    if (!el) return;
    const updateHeight = () => setControlsBarHeight(el.getBoundingClientRect().height);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useDockSnap(controlsBarRef, { enabled: !window.matchMedia(MOBILE_MEDIA_QUERY).matches });
  const skipNextDeckNameCommitRef = useRef(false);
  
  // Autocomplete State
  const inputRef = useRef(null);
  const [addCardSearchTerm, setAddCardSearchTerm] = useState('');
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const addCardSearchTimeoutRef = useRef(null);
  // Laufnummer der Autocomplete-Anfragen: verspätete Antworten älterer
  // Eingaben dürfen die stehende Liste nicht überschreiben.
  const addCardSearchSeqRef = useRef(0);
  
  const matchingCardIds = useDeckSyntaxSearch({ deckId: selectedDeck?.id, searchTerm });

  const handleDropError = useCallback((message) => {
    if (!message) return;
    pushToast({ tone: 'error', message });
  }, [pushToast]);

  // Labels
  const {
    deckLabels,
    getCardLabels,
    createAndAssignLabel,
    assignLabel: assignLabelToCard,
    removeLabel: removeLabelFromCard,
  } = useDeckLabels(
    selectedDeck?.id,
    selectedDeck?.editorSettings?.labels,
    selectedDeck?.cards,
    handleDropError
  );
  const [newLabelModal, setNewLabelModal] = useState(null);

  const handleNewLabelRequest = useCallback((deckCardEntryId) => {
    setNewLabelModal({ deckCardEntryId });
  }, []);

  const handleNewLabelConfirm = useCallback(async (name, color) => {
    if (newLabelModal) {
      const result = await createAndAssignLabel(name, color, newLabelModal.deckCardEntryId);
      if (!result?.ok) pushToast({ tone: 'error', message: result?.error || 'Label konnte nicht erstellt werden.' });
    }
    setNewLabelModal(null);
  }, [newLabelModal, createAndAssignLabel, pushToast]);
  
  // ========== SHARED DnD STATE ==========
  const [showExportModal, setShowExportModal] = useState(false);
  // Token-Modus, mit dem das Export-Modal öffnet: 'none' (oberer Export) bzw.
  // 'only' (Export-Button der Token-Kachel).
  const [exportTokenMode, setExportTokenMode] = useState('none');
  const [detail, setDetail] = useState(null); // { scryfallId, oracleId, section, fallback }
  const [showExcluded, setShowExcluded] = useState(true);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [activeCatalogEntry, setActiveCatalogEntry] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const viewDndHandlers = useRef({});
  // Vorab berechnete Lande-Position für die Drop-Animation. Wird in handleDragEnd
  // per DOM-Messung der existierenden Karten gesetzt und in dropAnimation.keyframes
  // gelesen. Bei null (Drop außerhalb / Same-Category-Drop) snapt das Overlay zur Quelle.
  const dropTargetRectRef = useRef(null);
  // Animations-Outcome für die dropAnimation.keyframes-Funktion. Werte:
  //   'land'      — Overlay fliegt zur in dropTargetRectRef.current vorab
  //                 gemessenen Lande-Position (Placeholder oder Commander-Slot)
  //   'snap-back' — Overlay snapt zur Quelle (Default — Same-Cat, kein Over,
  //                 Drop außerhalb)
  //   'fade-out'  — Overlay blendet am Cursor-Punkt aus (Drop in kollabierte
  //                 Table-Kategorie: Placeholder ist dann nicht im DOM)
  // Default 'snap-back' ist sicherer Fallback für den theoretisch unmöglichen
  // Fall, dass keyframes ohne vorherigen handleDragEnd-Call aufgerufen wird.
  const dropOutcomeRef = useRef('snap-back');
  // Patch der scrollIntoView-Methode auf der Source-Karten-DOM-Node.
  // Hintergrund: @dnd-kit/core ruft beim Drop hartverdrahtet
  // scrollIntoViewIfNeeded(activeNode) auf, wenn die Source-Karte off-screen
  // ist (core.esm.js Z. 3795). Das zwingt den Browser, die Source in den
  // Viewport zu scrollen — wirkt als unerwünschter Page-Snap zurück zur
  // Drag-Start-Position, wenn der User während Drag scrollt. Workaround:
  // bei handleDragStart scrollIntoView auf der Source zum no-op patchen,
  // in dropAnimation.keyframes (läuft NACH scrollIntoViewIfNeeded) restoren.
  // { el, original } | null
  const dragSourceScrollPatchRef = useRef(null);

  // Sensors for DnD
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );
  
  // Callback for views to register their DnD handlers
  const handleRegisterDndHandlers = useCallback((handlers) => {
    viewDndHandlers.current = handlers;
  }, []);

  const canEditDeck = Boolean(isOwnProfile && selectedDeck && selectedDeck.source !== 'physical');

  // Ausgeschlossene Kategorien aus den gespeicherten editorSettings berechnen
  const excludedCategoryNames = useMemo(() => {
    const cats = selectedDeck?.editorSettings?.categories;
    if (!Array.isArray(cats)) return new Set();
    return new Set(cats.filter(c => c.excluded).map(c => c.name?.toLowerCase()).filter(Boolean));
  }, [selectedDeck?.editorSettings?.categories]);

  // Playtest aus dem Editor: eigene Lobby anlegen, dieses Deck wählen, starten, direkt zum Board.
  const handleStartPlaytest = useCallback(async () => {
    if (!selectedDeck?.id || playtestLoading) return;
    try {
      setPlaytestLoading(true);
      const hasCommander = (selectedDeck?.cards || []).some(c => c.isCommander);
      let sessionId;
      try {
        const createResp = await apiClient.post('/api/playtest/create', { initialLife: hasCommander ? 40 : 20 });
        sessionId = createResp.data.sessionId;
      } catch (err) {
        // Eine offene Lobby existiert schon — dorthin, statt eine zweite zu erzwingen.
        if (err?.code === 'ALREADY_HOSTING' && err?.details?.sessionId) {
          navigate(`/playtest/lobby/${err.details.sessionId}`);
          return;
        }
        throw err;
      }
      await apiClient.post(`/api/playtest/${sessionId}/select-deck`, { deckId: selectedDeck.id });
      await apiClient.post(`/api/playtest/${sessionId}/start`);
      navigate(`/playtest/board/${sessionId}`, { state: { returnTo: location.pathname } });
    } catch (err) {
      console.error('Fehler beim Starten des Playtests:', err);
      pushToast({ tone: 'error', message: errorMessage(err, 'Playtest konnte nicht gestartet werden.') });
    } finally {
      setPlaytestLoading(false);
    }
  }, [selectedDeck, location, playtestLoading, navigate, pushToast]);

  const cardActions = useDeckCardActions({
    deck: selectedDeck,
    canEdit: canEditDeck,
    canMove: isOwnProfile && Boolean(selectedDeck),
    onDeckUpdate,
    onError: handleDropError,
  });
  const { handleQuantityDelta, addCardByName, addCardByScryfallId, fetchAutocomplete } = cardActions;

  const undo = useDeckUndo({
    deckId: selectedDeck?.id,
    cardActions,
    assignLabel: assignLabelToCard,
    getCardLabels,
  });

  // Registriert Undo-Paare für Mengen-Änderungen; Minus-auf-0 ist ein Entfernen-Paar
  // (das PATCH schlüge auf der gelöschten Zeile fehl) und bekommt den Entfernen-Toast.
  const handleQuantityDeltaWithUndo = useCallback(async (scryfallId, delta) => {
    const result = await handleQuantityDelta(scryfallId, delta);
    if (!result?.ok || !result.card) return;
    if (result.card.isCommander) return; // Commander-Aktionen sind keine Undo-Paare
    if (result.removed) {
      const entryId = undo.registerRemove(result.card, result.previousQuantity);
      pushToast({
        tone: 'success',
        message: `${result.card.name} entfernt`,
        action: { label: 'Rückgängig', onClick: () => undo.undoEntry(entryId) },
      });
    } else {
      undo.registerQuantity(scryfallId, delta);
    }
  }, [handleQuantityDelta, undo, pushToast]);

  const handleCardMoved = useCallback(({ cardId, from }) => {
    undo.registerMove({ cardId, from });
  }, [undo]);

  // Der Katalog gibt es nur für eigene virtuelle Decks; physische Decks (canEditDeck
  // false) sehen weder Spalte noch Einstiege.
  const catalogDeckId = useMemo(() => deckNumericId(selectedDeck), [selectedDeck]);
  const catalogSide = bridgeSide === 'left' ? 'right' : 'left';
  // Startwert der Katalog-Suche: die Farben des Commanders, wie im mobilen Hinzufügen-Modus.
  const catalogColorIdentity = useMemo(
    () => commanderColorFilter(selectedDeck?.cards),
    [selectedDeck?.cards],
  );
  const dropSuggestion = useDropSuggestionFromCache(catalogDeckId);

  /**
   * Öffnet die Katalog-Spalte, optional auf einem bestimmten Reiter.
   * Einstiege: der Fußzeilen-Eintrag des ＋-Docks („Erweiterte Suche…") und die
   * Vorschlags-Sektion der Brücke.
   */
  const openCatalog = useCallback((tab) => {
    if (!canEditDeck) return;
    if (tab) setCatalogTab(tab);
    setCatalogStageTwo(false);
    setCatalogOpen(true);
  }, [canEditDeck, setCatalogTab]);

  // Stufe 2 endet mit der Spalte: sie beschreibt die Aufteilung zwischen Spalte und Playmat,
  // und ohne die Spalte gibt es nichts aufzuteilen — bliebe das Flag stehen, hätte die
  // Playmat nach dem ✕ null Breite und der nächste Aufruf öffnete sofort bildschirmfüllend.
  const closeCatalog = useCallback(() => {
    setCatalogStageTwo(false);
    setCatalogOpen(false);
  }, []);

  // Gemeinsamer Weg beider Katalog-Wege (Plus und Ablage): hinzufügen, Undo-Eintrag
  // registrieren, die frische Karte kurz hervorheben.
  const addCardFromCatalog = useCallback(async (scryfallId, { categoryId } = {}) => {
    const result = await addCardByScryfallId(scryfallId, { categoryId });
    let entryId = null;
    if (result?.card) {
      entryId = undo.registerAdd(result.card);
      setHighlightedNewCardId(result.card.id || result.card.scryfallId);
      setTimeout(() => setHighlightedNewCardId(null), 5000);
    }
    // Ob neu oder Dublette: die Karte liegt im Deck und gehört nicht mehr in die
    // Rangliste. Deckt Ablage und Brücken-Plus ab; der Katalog entnimmt zusätzlich
    // selbst, weil er auch ohne diesen Rahmen läuft (Zweitfenster).
    if (result) dropSuggestion({ id: scryfallId, oracleId: result.card?.oracleId });
    return { result, entryId };
  }, [addCardByScryfallId, undo, dropSuggestion]);

  // Plus-Knopf im Katalog: Erfolg und Dublette meldet der Katalog selbst (sein Vertrag).
  // Die Gegenaktion geht mit der Antwort mit — nur so kann sein Toast einen
  // „Rückgängig"-Knopf zeigen, den es im Zweitfenster ohne Stapel nicht gibt.
  const handleCatalogAddCard = useCallback(async (scryfallId, { categoryId } = {}) => {
    const { result, entryId } = await addCardFromCatalog(scryfallId, { categoryId });
    if (!result || entryId == null) return result;
    return { ...result, undo: () => undo.undoEntry(entryId) };
  }, [addCardFromCatalog, undo]);

  // Ablage einer Katalog-Kachel auf einer Kategorie. Die Commander-Zone nimmt keine
  // Katalog-Karte an: „hinzufügen und zum Commander machen" wären zwei Mutationen, deren
  // zweite fehlschlagen kann (Farbidentität, zweiter Commander) — die Karte läge dann
  // irgendwo, ohne dass der Zug das erklärt.
  const handleCatalogCardDrop = useCallback(async (scryfallId, { categoryId, commander, unsupportedGrouping, entry } = {}) => {
    if (!canEditDeck) return;
    if (commander) {
      pushToast({ tone: 'info', message: 'Zieh die Karte in eine Kategorie — den Commander setzt du danach über das Karten-Menü.' });
      return;
    }
    if (unsupportedGrouping) {
      pushToast({ tone: 'info', message: 'Diese Gruppierung hat keine echten Kategorien — stell auf „Kategorien" um oder nimm das Plus auf der Kachel.' });
      return;
    }
    const { result, entryId } = await addCardFromCatalog(scryfallId, { categoryId });
    notifyAddResult(pushToast, result, {
      name: entry?.name,
      onUndo: entryId != null ? () => undo.undoEntry(entryId) : undefined,
    });
  }, [canEditDeck, addCardFromCatalog, pushToast, undo]);

  // Plus-Knopf der Vorschläge-Brückensektion — derselbe Toast-/Undo-Ablauf wie beim
  // Ablegen aus dem Katalog (`handleCatalogCardDrop`), nur ohne Ziel-Kategorie/Commander:
  // die Brücke bietet keine Kategoriewahl an, die Karte landet automatisch. Die Rückgabe
  // hält die Kachel im Lade-Zustand, bis der Server geantwortet hat.
  const handleSuggestionAdd = useCallback(async (entry) => {
    if (!canEditDeck) return null;
    const { result, entryId } = await addCardFromCatalog(entry.id, {});
    notifyAddResult(pushToast, result, {
      name: entry?.name,
      onUndo: entryId != null ? () => undo.undoEntry(entryId) : undefined,
    });
    return result;
  }, [canEditDeck, addCardFromCatalog, pushToast, undo]);

  const { mutate: updateCardMetadata } = useUpdateCardMetadata();

  // Die lebende Editor-Karte zum geöffneten Modal; nach einem Print-Wechsel unter der neuen ID.
  const detailCard = useMemo(() => (detail
    ? (selectedDeck?.cards || []).find((c) => (c.scryfallId || c.id) === detail.scryfallId) ?? null
    : null), [detail, selectedDeck]);

  const openCardDetail = useCallback((card, section = 'card') => {
    if (!card?.oracleId) return;
    setDetail({
      scryfallId: card.scryfallId || card.id,
      oracleId: card.oracleId,
      section,
      // Kopf/Bild, bis der Umschlag da ist — und in dem Moment, in dem der Refetch die Karte kurz nicht liefert.
      fallback: { oracleId: card.oracleId, name: card.name, typeLine: card.typeLine, imageUrl: card.imageUrl, imageUrlBack: card.imageUrlBack || undefined, imageOrientation: card.imageOrientation },
    });
  }, []);
  const handleShowDetails = useCallback((card) => openCardDetail(card, 'card'), [openCardDetail]);
  const handleSwitchPrint = useCallback((card) => openCardDetail(card, 'prints'), [openCardDetail]);

  const handleUnlockPrint = useCallback(() => {
    const entryId = detailCard?.deckCardEntryId;
    if (!entryId) return;
    // Fehler meldet MutationErrorToasts
    updateCardMetadata(
      { deckCardId: entryId, metadata: { printLocked: false } },
      { onSuccess: () => pushToast({ tone: 'success', message: 'Print freigegeben' }) },
    );
  }, [detailCard, updateCardMetadata, pushToast]);

  const handleExternalDrop = useCallback(async (categoryId, transfer) => {
    if (!canEditDeck) return;
    const reference = extractCardReferenceFromDropData(transfer);
    if (!reference) return;
    try {
      const options = typeof categoryId === 'number' ? { categoryId } : {};
      const result = reference.scryfallId
        ? await addCardByScryfallId(reference.scryfallId, options)
        : await addCardByName(reference.cardName, options);
      if (result?.card) {
        const card = result.card;
        const entryId = undo.registerAdd(card);
        setHighlightedNewCardId(card.id || card.scryfallId);
        pushToast({ tone: 'success', message: `${card.name} hinzugefügt`, action: { label: 'Rückgängig', onClick: () => undo.undoEntry(entryId) } });
        setTimeout(() => setHighlightedNewCardId(null), 5000);
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-card-id="${card.id || card.scryfallId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } else if (result?.duplicate) {
        pushToast({ tone: 'info', message: `${reference.cardName ?? 'Karte'} ist bereits im Deck` });
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Karte per Drop:', error);
    }
  }, [canEditDeck, addCardByName, addCardByScryfallId, pushToast, undo]);

  const externalDrop = useExternalCardDrop({ enabled: canEditDeck, onDrop: handleExternalDrop });

  // Handler für Kategorie hinzufügen (wird an AddCategoryDialog weitergereicht).
  // Nutzt in BEIDEN Fällen (excluded und nicht-excluded) das echte, sofort-POSTende
  // addCategory aus useCategoryManagement (via viewDndHandlers) — das legt die Kategorie
  // mit echter ID im Backend an und trägt sie in den State ein, aus dem useDeckViewState
  // die groups baut. Vorher hing der nicht-excluded-Zweig nur den Namen an den
  // (mittlerweile entfernten) customCategories-State — kein POST, keine Anzeige.
  //
  // Zusätzlich: die neue CategoryDTO (inkl. `excluded`-Flag) sofort in den Deck-Cache
  // mergen (editorSettings.categories). Ohne das lesen excludedCategoryNames (oben) und
  // useDeckViewState.savedExcludedNames weiterhin den ALTEN Cache-Stand — eine frisch als
  // "excluded" angelegte Kategorie würde dann bis zum Reload trotzdem in die
  // Statistiken (Manakurve, Kartenzahl, Hypergeometrisch) eingerechnet, obwohl die Karte
  // in der UI schon korrekt in der excluded-Gruppe liegt (die hängt nur am lokalen
  // useCategoryManagement-State, nicht am Cache).
  const handleAddCategory = useCallback(async (name, excluded = false) => {
    const category = await viewDndHandlers.current.addCategory?.(name, excluded);
    if (category) {
      onDeckUpdate?.((prev) => (prev ? mergeCategoryIntoDeckCache(prev, category) : prev));
    }
  }, [onDeckUpdate]);

  // Nach erfolgreichem Kategorie-Rename: Deck-Cache nachziehen (Karten referenzieren
  // die Kategorie per Name; editorSettings.categories speist excludedCategoryNames).
  const handleCategoryRenamed = useCallback((oldName, result) => {
    if (result?.ok && result.category) {
      const updated = result.category;
      onDeckUpdate?.((prev) => {
        if (!prev) return prev;
        const cards = (prev.cards || []).map((card) =>
          card.categoryId === updated.id ||
          (card.category && card.category.toLowerCase() === oldName.toLowerCase())
            ? { ...card, categoryId: updated.id, category: updated.name }
            : card
        );
        const savedCats = prev.editorSettings?.categories;
        const categories = Array.isArray(savedCats)
          ? savedCats.map((c) => (c.id === updated.id ? { ...c, name: updated.name } : c))
          : savedCats;
        return { ...prev, cards, editorSettings: { ...prev.editorSettings, categories } };
      });
      return;
    }
    if (result?.conflict) {
      pushToast({ tone: 'error', message: 'Eine Kategorie mit diesem Namen existiert bereits.' });
    } else if (result) {
      pushToast({ tone: 'error', message: 'Kategorie konnte nicht umbenannt werden.' });
    }
  }, [onDeckUpdate, pushToast]);

  // Entfernt alle Karten einer Kategorie aus dem lokalen Deck-State
  const handleRemoveCardsByCategory = useCallback((categoryName) => {
    if (!onDeckUpdate) return;
    onDeckUpdate((prev) => {
      if (!prev) return prev;
      const updatedCards = (prev.cards || []).filter(
        (card) => card.category?.toLowerCase() !== categoryName.toLowerCase()
      );
      return { ...prev, cards: updatedCards };
    });
  }, [onDeckUpdate]);

  // Handler für Kategorie-Änderungen - aktualisiert lokalen State
  // categoryLike = { id, name, isCommander } | null
  const handleCategoryChange = useCallback((cardId, categoryLike) => {
    if (!onDeckUpdate) return;
    onDeckUpdate((prev) => {
      if (!prev) return prev;
      const updatedCards = (prev.cards || []).map((card) =>
        card.id === cardId
          ? {
              ...card,
              categoryId: categoryLike?.id ?? null,
              category: categoryLike?.name ?? null,
              isCommander: !!categoryLike?.isCommander,
            }
          : card
      );
      return { ...prev, cards: updatedCards };
    });
  }, [onDeckUpdate]);

  // Handler für Commander entfernen
  const handleRemoveCommander = useCallback(async (cardId) => {
    if (!selectedDeck || !onDeckUpdate) return;

    // Optimistisch: Kategorie auf null setzen (zurück zu Typ-basierter Kategorisierung)
    let previousCategoryLike;
    onDeckUpdate((prev) => {
      if (!prev) return prev;
      const updatedCards = (prev.cards || []).map((card) => {
        if (card.id !== cardId) return card;
        previousCategoryLike = { id: card.categoryId, name: card.category, isCommander: card.isCommander };
        return { ...card, categoryId: null, category: null, isCommander: false };
      });
      return { ...prev, cards: updatedCards };
    });

    try {
      const response = await json(`/api/decks/${selectedDeck.id}/cards/${cardId}/category`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryId: null }),
      });
      // Server hat den Typ-Fallback (Kategorie nach Kartentyp) bestimmt
      handleCategoryChange(cardId, response?.category ?? null);
      // Neu entstandene Kategorie sofort im Cache verfügbar machen, sonst fehlt ihr
      // Header bis zum Reload (siehe useCategoryManagement-Sync-Effekt).
      if (response?.category) {
        onDeckUpdate((prev) => (prev ? mergeCategoryIntoDeckCache(prev, response.category) : prev));
      }
    } catch (error) {
      console.error('Fehler beim Entfernen des Commander-Status:', error);
      // Revert bei Fehler (nur betroffenes Feld)
      handleCategoryChange(cardId, previousCategoryLike);
      pushToast({ tone: 'error', message: 'Commander-Status konnte nicht geändert werden.' });
    }
  }, [selectedDeck, onDeckUpdate, handleCategoryChange, pushToast]);

  // ========== SHARED DnD HANDLERS ==========

  // Restore dnd-kit-induced scrollIntoView-Patch auf der Source-Karte.
  // Idempotent — sicher mehrfach aufrufbar. Siehe dragSourceScrollPatchRef
  // für Hintergrund.
  const restoreSourceScrollPatch = () => {
    const patch = dragSourceScrollPatchRef.current;
    if (patch && patch.el && patch.original) {
      patch.el.scrollIntoView = patch.original;
    }
    dragSourceScrollPatchRef.current = null;
  };

  // Patcht scrollIntoView auf der Source-DOM-Node zum No-op und merkt sich das Original in
  // dragSourceScrollPatchRef, damit restoreSourceScrollPatch es zurückstellen kann. Kein
  // Effekt, wenn el fehlt oder keine scrollIntoView-Methode trägt.
  const patchScrollIntoView = (el) => {
    if (el && typeof el.scrollIntoView === 'function') {
      const original = el.scrollIntoView;
      el.scrollIntoView = function noopDuringDrag() {};
      dragSourceScrollPatchRef.current = { el, original };
    }
  };

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    const activeId = active.id;

    if (typeof activeId === 'string' && activeId.startsWith('cat-')) {
      setActiveCategoryId(activeId);
    } else if (parseCatalogCardId(activeId)) {
      // Katalog-Kachel: die Karte liegt noch nicht im Deck, es gibt keinen Quell-Knoten
      // auf der Playmat — nur das Overlay braucht ihre Daten.
      setActiveCatalogEntry(active.data.current?.entry ?? null);

      // dnd-kit ruft beim Drop scrollIntoViewIfNeeded(activeNode) auf, wenn die Source
      // off-screen ist, und scrollt die Page zurück zur Drag-Start-Position. Restore in
      // dropAnimation.keyframes bzw. im handleDragEnd-Cleanup-Timeout. Siehe
      // dragSourceScrollPatchRef. Ohne den Reset hier bliebe ein durch Escape abgebrochener
      // Drag als stehender Patch liegen und der nächste Katalog-Drag patchte nichts mehr.
      restoreSourceScrollPatch();
      if (active.data.current?.type === 'catalog-card') {
        patchScrollIntoView(document.querySelector(`[data-drag-id="${window.CSS.escape(String(active.id))}"]`));
      }
    } else {
      const card = active.data.current?.card;
      if (card) setActiveCard(card);

      restoreSourceScrollPatch(); // Safety: alte Patches aufräumen
      const cardId = card?.id ?? card?.scryfallId;
      if (cardId != null) {
        patchScrollIntoView(document.querySelector(`[data-card-id="${window.CSS.escape(String(cardId))}"]`));
      }
    }

    // Delegate to active view's handler
    viewDndHandlers.current.onDragStart?.(event);
  }, []);
  
  const handleDragOver = useCallback((event) => {
    // Delegate to active view's handler
    viewDndHandlers.current.onDragOver?.(event);
  }, []);
  
  const handleDragEnd = useCallback((event) => {
    // Animations-Outcome aus DOM-State entscheiden:
    //   - Kein Over / Same-Cat: snap-back zur Quelle (Default)
    //   - Commander-Drop: land zur oberen linken Ecke des Commander-Slots
    //   - Standard-Drop: Placeholder im DOM finden → land zur Placeholder-Rect
    //   - Standard-Drop, Placeholder fehlt (z.B. kollabierte Table-Kategorie):
    //       fade-out am Cursor-Punkt
    //
    // Drop-Erfolg ist orthogonal: Backend-Rejection wird async durch
    // useDeckDragHandlers.handleDragEnd revertiert ("Karte springt zurück"),
    // unabhängig vom Animations-Outcome. Daher keine Client-Side-Validierung
    // an dieser Stelle.
    const overRect = event.over?.rect;
    const overCat = event.over?.data?.current?.category;
    const isCommanderDropTarget = isCommanderDrop(event.over);
    const activeCat = event.active?.data?.current?.card?.category;
    const isSameCat = !!activeCat && !!overCat &&
                      overCat.toLowerCase() === activeCat.toLowerCase();

    let landing = null;
    let outcome;

    if (!event.over || (!overCat && !isCommanderDropTarget) || isSameCat) {
      outcome = 'snap-back';
    } else if (isCommanderDropTarget) {
      // Commander-Slot hat keinen Placeholder; Top-Left ist exakt.
      landing = { left: overRect.left, top: overRect.top };
      outcome = 'land';
    } else {
      // CSS.escape gegen Sonderzeichen in Custom-Category-Namen, "i"-Flag
      // gegen Casing-Drift (Categories sind nicht garantiert lower-case).
      const slot = document.querySelector(
        `[data-placeholder-category="${window.CSS.escape(overCat)}" i]`
      );
      if (slot) {
        const rect = slot.getBoundingClientRect();
        landing = { left: rect.left, top: rect.top };
        outcome = 'land';
      } else {
        // Placeholder nicht im DOM → Kategorie ist kollabiert (Table) oder
        // CompactView mit !isCollapsed-Conditional. Sauberer Fade am
        // Cursor-Punkt statt versteckter Sprung.
        outcome = 'fade-out';
      }
    }

    dropTargetRectRef.current = landing;
    dropOutcomeRef.current = outcome;

    // View-Handler fire-and-forget; eigener try/catch im Hook behandelt Fehler.
    viewDndHandlers.current.onDragEnd?.(event);

    // setActiveCard(null) erst NACH der Drop-Animation. 50ms Buffer über
    // DROP_ANIMATION_DURATION_MS für sauberes Auslaufen der CSS-Animation.
    setTimeout(() => {
      setActiveCard(null);
      setActiveCatalogEntry(null);
      setActiveCategoryId(null);
      // Safety-net: falls keyframes nie lief (z.B. dnd-kit-Early-Return,
      // Drag-Cancel ohne Animation), Source-scrollIntoView trotzdem restoren.
      restoreSourceScrollPatch();
    }, DROP_ANIMATION_DURATION_MS + 50);
  }, []);

  // Drop-Animation: drei Outcomes — land (Placeholder-Lookup), snap-back (Quelle),
  // fade-out (kollabierte Kategorie). useMemo([]) ist korrekt: die keyframes-Funktion
  // liest dropOutcomeRef/dropTargetRectRef erst zur Animationszeit, nicht bei Render.
  const dropAnimation = useMemo(() => ({
    duration: DROP_ANIMATION_DURATION_MS,
    easing: 'ease-out',
    keyframes: ({ transform, dragOverlay }) => {
      // dnd-kit hat unmittelbar vor diesem Callback scrollIntoViewIfNeeded auf
      // der active node aufgerufen (core.esm.js Z. 3795). Unser scrollIntoView-
      // Patch auf der Source-Karte hat das zum no-op gemacht — jetzt restoren,
      // weil der gefährliche Moment vorbei ist. Siehe dragSourceScrollPatchRef.
      restoreSourceScrollPatch();

      // Wichtig: CSS.Transform.toString aus @dnd-kit/utilities komponiert auf
      // transform.initial/.final — reines `translate3d(...)` würde dnd-kits
      // internes Transform überschreiben und die Animation liefe unsichtbar.
      const outcome = dropOutcomeRef.current;
      const landing = dropTargetRectRef.current;

      if (outcome === 'land' && landing) {
        // Vorab gemessene Placeholder-Position (oder Commander-Slot).
        const overlayRect = dragOverlay.rect;
        const dx = landing.left - overlayRect.left;
        const dy = landing.top - overlayRect.top;
        const finalTransform = {
          ...transform.initial,
          x: transform.initial.x + dx,
          y: transform.initial.y + dy,
        };
        return [
          { transform: CSS.Transform.toString(transform.initial) },
          { transform: CSS.Transform.toString(finalTransform) },
        ];
      }

      if (outcome === 'fade-out') {
        // Transform explizit setzen, damit das Overlay sicher am Cursor-Punkt
        // ausfadet (defensiv gegen Web-Animations-API-Reset auf computed Style).
        return [
          { transform: CSS.Transform.toString(transform.initial), opacity: 1 },
          { transform: CSS.Transform.toString(transform.initial), opacity: 0 },
        ];
      }

      // snap-back (Default): explizite Animation zur Quelle, identisch zum
      // heutigen Verhalten. transform.final ist dnd-kits berechnete
      // Quell-Position. return undefined hat unklare dnd-kit-Semantik und
      // könnte die Animation überspringen statt zur Quelle zu snappen.
      return [
        { transform: CSS.Transform.toString(transform.initial) },
        { transform: CSS.Transform.toString(transform.final) },
      ];
    },
  }), []);
  
  // Collision detection
  const categoryCollisionDetection = useCallback((args) => {
    const collisions = rectIntersection(args);
    return collisions.filter(c => typeof c.id === 'string' && c.id.startsWith('cat-'));
  }, []);
  
  const collisionDetection = activeCategoryId ? categoryCollisionDetection : pointerWithin;

  // Draft für Decknamen immer mit ausgewähltem Deck synchron halten
  useEffect(() => {
    setDeckNameDraft(selectedDeck?.name || '');
    setIsEditingDeckName(false);
  }, [selectedDeck?.id, selectedDeck?.name]);

  const commitDeckRename = useCallback(async () => {
    if (!canEditDeck || !selectedDeck) return;
    const trimmed = (deckNameDraft || '').trim();
    if (!trimmed || trimmed === (selectedDeck.name || '').trim()) return;

    const prevName = selectedDeck.name;
    // Optimistisch aktualisieren
    onDeckUpdate?.((prev) => (prev ? { ...prev, name: trimmed } : prev));
    try {
      await json(`/api/decks/${selectedDeck.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newName: trimmed }),
      });
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Decknamens:', err);
      // Revert
      onDeckUpdate?.((prev) => (prev ? { ...prev, name: prevName } : prev));
      setDeckNameDraft(prevName || '');
    }
  }, [canEditDeck, selectedDeck, deckNameDraft, onDeckUpdate]);

  const openDeckNameEditor = useCallback(() => {
    if (!canEditDeck) return;
    setIsEditingDeckName(true);
    // Fokus nach Render
    setTimeout(() => {
      deckNameInputRef.current?.focus?.();
      deckNameInputRef.current?.select?.();
    }, 0);
  }, [canEditDeck]);

  const handleDeckNameBlur = useCallback(async () => {
    setIsEditingDeckName(false);
    if (skipNextDeckNameCommitRef.current) {
      skipNextDeckNameCommitRef.current = false;
      return;
    }
    await commitDeckRename();
  }, [commitDeckRename]);

  // Handler für Mana-Kurven-Filter (klick auf Balken)
  const handleManaCurveClick = useCallback((cmc) => {
    setHighlightedCmc(prev => prev === cmc ? null : cmc);
  }, []);
  
  // Autocomplete Handler
  const handleAddCardSearchChange = useCallback((e) => {
    const query = e.target.value;
    setAddCardSearchTerm(query);
    // Die Ergebnisliste bleibt stehen, bis die neue Antwort da ist — kein
    // Flackern bei jedem Tastendruck. Die Markierung wird zurückgesetzt, sonst
    // könnte Enter im Debounce-Fenster einen veralteten Eintrag auswählen.
    setShowAutocomplete(query.length > 0);
    setHighlightedIndex(-1);
    if (query.length === 0) {
      setAutocompleteResults([]);
    }

    if (addCardSearchTimeoutRef.current) {
      clearTimeout(addCardSearchTimeoutRef.current);
    }

    if (query.length > 0) {
      const seq = ++addCardSearchSeqRef.current;
      addCardSearchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await fetchAutocomplete(query);
          if (seq !== addCardSearchSeqRef.current) return; // verspätete Antwort einer älteren Eingabe
          setAutocompleteResults((results || []).slice(0, 6));
          setHighlightedIndex(-1);
        } catch (err) {
          console.error('Fehler beim Abrufen der Autovervollständigung:', err);
          if (seq === addCardSearchSeqRef.current) setAutocompleteResults([]);
        }
      }, AUTOCOMPLETE_DEBOUNCE_MS);
    }
  }, [fetchAutocomplete]);

  const handleAutocompleteSelect = useCallback(async (suggestion) => {
    const cardName = suggestion?.name ?? '';
    setAddCardSearchTerm(cardName);
    setAutocompleteResults([]);
    setShowAutocomplete(false);

    if (!selectedDeck) {
      console.error('Kein Deck ausgewählt, um die Karte hinzuzufügen.');
      return;
    }

    try {
      // Suggestion mit ID (lokaler Treffer) → direkter ID-Add ohne Scryfall;
      // ohne ID (Scryfall-Fallback) → Namens-Add wie bisher.
      const result = suggestion?.scryfallId
        ? await addCardByScryfallId(suggestion.scryfallId)
        : await addCardByName(cardName);
      setAddCardSearchTerm('');
      if (result?.card) {
        const card = result.card;
        const entryId = undo.registerAdd(card);
        setHighlightedNewCardId(card.id || card.scryfallId);
        pushToast({
          tone: 'success',
          message: `${card.name} hinzugefügt`,
          action: { label: 'Rückgängig', onClick: () => undo.undoEntry(entryId) },
        });
        setTimeout(() => setHighlightedNewCardId(null), 5000);
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-card-id="${card.id || card.scryfallId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        // Beiwerk, nie Blocker: der Hinweis-Toast bleibt aus, wenn die Abfrage
        // fehlschlägt oder die Suggestion keine scryfallId trägt (Namens-Pfad).
        if (suggestion?.scryfallId) {
          json(`/api/wantlist/friends-ownership/${encodeURIComponent(suggestion.scryfallId)}`)
            .then(({ friends }) => {
              if (friends?.length) {
                const f = friends[0];
                pushToast({
                  tone: 'info',
                  message: `${f.name} besitzt diese Karte (${f.quantity}×)${friends.length > 1 ? ` — und ${friends.length - 1} weitere` : ''}`,
                });
              }
            })
            .catch(() => {});
        }
      } else if (result?.duplicate) {
        pushToast({ tone: 'info', message: `${cardName} ist bereits im Deck` });
      }
    } catch (err) {
      console.error('Fehler beim Hinzufügen der Karte zum Deck:', err);
    }
  }, [selectedDeck, addCardByName, addCardByScryfallId, pushToast, undo]);

  const handleKeyDown = useCallback((event) => {
    if (!showAutocomplete || autocompleteResults.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % autocompleteResults.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) => (
          prev === -1 ? autocompleteResults.length - 1 : (prev - 1 + autocompleteResults.length) % autocompleteResults.length
        ));
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex !== -1) handleAutocompleteSelect(autocompleteResults[highlightedIndex]);
        break;
      case 'Escape':
        setShowAutocomplete(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  }, [showAutocomplete, autocompleteResults, highlightedIndex, handleAutocompleteSelect]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addCardSearchTimeoutRef.current) {
        clearTimeout(addCardSearchTimeoutRef.current);
      }
    };
  }, []);

  // Strg+Z/Cmd+Z: oberster Undo-Eintrag. Nicht in Eingabefeldern — dort gehört das
  // native Text-Undo dem Browser (Suchfeld, Inline-Rename, Modal-Inputs).
  useEffect(() => {
    if (!canEditDeck) return undefined;
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) return;
      if ((event.key || '').toLowerCase() !== 'z') return;
      const target = event.target;
      if (target && typeof target.closest === 'function'
        && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (!undo.canUndo) return;
      event.preventDefault();
      undo.undoLast();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canEditDeck, undo]);

  const handleAddCardBlur = useCallback(() => {
    setTimeout(() => setShowAutocomplete(false), 200);
  }, []);

  const handleDeckNameKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      skipNextDeckNameCommitRef.current = true;
      setDeckNameDraft(selectedDeck?.name || '');
      e.currentTarget.blur();
    }
  }, [selectedDeck?.name]);

  // Karten ohne Excluded-Kategorien für Stats/ManaCurve
  const statsCards = useMemo(
    () => (selectedDeck?.cards || []).filter(c => !excludedCategoryNames.has(c.category?.toLowerCase() ?? '')),
    [selectedDeck?.cards, excludedCategoryNames]
  );

  // Zentrale Deck-Statistiken (wird von ManaCurve, ManaBaseSection etc. genutzt)
  const deckStats = useDeckStats(statsCards);

  // Kartenzahl + Gesamtwert für die EditorActionBar-Stats
  const deckTotals = useMemo(() => computeDeckTotals(statsCards), [statsCards]);

  // Fortschritts-Ziel der Brücke: 100 Karten nur bei Commander-Decks, sonst reine Zählung
  const bridgeTarget = useMemo(
    () => ((selectedDeck?.cards || []).some((c) => c.isCommander) ? 100 : null),
    [selectedDeck?.cards]
  );

  // Anzahl für die Tokens-Sprungzeile — dieselbe Dedup-Regel wie die Kachel selbst.
  const uniqueTokenCount = useMemo(
    () => countUniqueTokens(selectedDeck?.tokens),
    [selectedDeck?.tokens],
  );

  if (!selectedDeck) {
    return (
      <div style={{ 
        padding: '20px',
        color: 'var(--text-secondary)',
        textAlign: 'center',
      }}>
        Kein Deck ausgewählt
      </div>
    );
  }

  return (
    <>
    <Page width="full">
    {/* Kopf seitenweit — beschreibt das ganze Deck, nicht nur den Kartenbereich
        (der links von der Brücke geteilt wird). */}
    <EditorActionBar
      deckName={selectedDeck.name}
      isPhysical={selectedDeck.source === 'physical'}
      canEditDeck={canEditDeck}
      isOwnProfile={isOwnProfile}
      isEditingDeckName={isEditingDeckName}
      deckNameDraft={deckNameDraft}
      deckNameInputRef={deckNameInputRef}
      onDeckNameChange={setDeckNameDraft}
      onDeckNameBlur={handleDeckNameBlur}
      onDeckNameKeyDown={handleDeckNameKeyDown}
      onOpenDeckNameEditor={openDeckNameEditor}
      cardCount={deckTotals.count}
      totalValue={deckTotals.value}
      onExport={() => { setExportTokenMode('none'); setShowExportModal(true); }}
      onStartPlaytest={handleStartPlaytest}
      playtestLoading={playtestLoading}
    />
    <div
      data-testid="deck-editor-page"
      {...externalDrop.rootProps}
      style={{ display: 'flex', width: '100%', position: 'relative', overflowX: 'clip' }}
    >
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
    <CommandBridge side={bridgeSide}>
      <CommanderStack
        cards={selectedDeck.cards || []}
        deckId={selectedDeck?.id}
        isOwnProfile={isOwnProfile}
        canEditDeck={canEditDeck}
        onRemoveCommander={handleRemoveCommander}
        onShowDetails={handleShowDetails}
        onSwitchPrint={canEditDeck ? handleSwitchPrint : undefined}
        isDraggingCard={!!activeCard}
      />
      <BridgeSection.Static>
        <div className="bridge-progress">
          <span className={`bridge-progress__num${bridgeTarget && deckTotals.count >= bridgeTarget ? ' bridge-progress__num--done' : ''}`}>
            <strong>{deckTotals.count}</strong>{bridgeTarget ? ` / ${bridgeTarget}` : ' Karten'}
          </span>
          {bridgeTarget && (
            <ProgressBar value={Math.min(deckTotals.count, bridgeTarget)} max={bridgeTarget} label={`${deckTotals.count} von ${bridgeTarget} Karten`} />
          )}
        </div>
      </BridgeSection.Static>
      {canEditDeck && (
        <SuggestionSection
          deckId={catalogDeckId}
          open={suggestionsOpen}
          onToggle={() => setSuggestionsOpen((o) => !o)}
          onAdd={handleSuggestionAdd}
          onOpenCatalog={() => openCatalog('suggestions')}
          onOpenCard={openCardDetail}
          initialColorIdentity={catalogColorIdentity}
        />
      )}
      <BridgeSection
        label="Mana-Kurve"
        value={deckStats.avgCmc != null ? `Ø ${deckStats.avgCmc.toFixed(1).replace('.', ',')}` : null}
        open={curveOpen} onToggle={() => setCurveOpen((o) => !o)}
      >
        <ManaCurve cards={statsCards} deckStats={deckStats} onBarClick={handleManaCurveClick} highlightedCmc={highlightedCmc} />
      </BridgeSection>
      <BridgeSection
        label="Ziehchancen"
        actions={(
          <InfoHint title="Ziehchancen">
            <p>
              Wahrscheinlichkeit, in der Starthand bzw. nach N gezogenen Karten mindestens K Karten
              der gewählten Gruppe zu sehen (hypergeometrische Verteilung). Nicht-im-Deck-Kategorien
              zählen nicht mit.
            </p>
          </InfoHint>
        )}
        open={oddsOpen} onToggle={() => setOddsOpen((o) => !o)}
      >
        <HypergeometricStats cards={statsCards} excludedCategoryNames={excludedCategoryNames} />
      </BridgeSection>
      <BridgeSection
        label="Mana-Basis" value={`${deckStats.landCount ?? 0} Länder`}
        open={manaBaseOpen} onToggle={() => setManaBaseOpen((o) => !o)}
      >
        <ManaBaseSection deckStats={deckStats} canEdit={canEditDeck} deckId={selectedDeck?.id} onDeckUpdate={onDeckUpdate} />
      </BridgeSection>
      <WantlistRow deck={selectedDeck} isOwnProfile={isOwnProfile} />
      {uniqueTokenCount > 0 && (
        <BridgeSection
          label={`Tokens (${uniqueTokenCount})`}
          value={<ArrowDown size={13} aria-hidden />}
          onToggle={() => tokenSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        />
      )}
    </CommandBridge>
    {canEditDeck && catalogOpen && (
      <CatalogPanel
        side={catalogSide}
        width={catalogWidth}
        onWidthChange={setCatalogWidth}
        stageTwo={catalogStageTwo}
        onStageTwoChange={setCatalogStageTwo}
        onClose={closeCatalog}
        deckId={catalogDeckId}
        activeTab={catalogTab}
        onTabChange={setCatalogTab}
        onAddCard={handleCatalogAddCard}
        isAdmin={isAdmin}
        dndEnabled={!catalogStageTwo}
        initialColorIdentity={catalogColorIdentity}
        openInWindowHref={`/decks/${encodeURIComponent(selectedDeck.id)}/katalog`}
      />
    )}
    <div
      ref={playmatRef}
      style={{
        // Stufe 2: die Playmat bleibt gemountet (Scroll-Position, Messungen), gibt aber
        // ihre ganze Fläche an den Katalog ab.
        flex: catalogStageTwo ? '0 0 0px' : 1,
        minWidth: 0,
        boxSizing: 'border-box',
        // Verhindert horizontale Scrollbars durch Rundung/Zoom/Padding-Kombinationen
        overflowX: 'clip',
        overflowY: catalogStageTwo ? 'hidden' : undefined,
        borderRadius: '6px',
      }}
    >
      <div className="de-controls" ref={controlsBarRef}>
        <EditorFilterStrip
          searchTerm={searchTerm} onSearchTermChange={setSearchTerm}
          sortBy={sortBy} onSortByChange={setSortBy} sortOptions={SORT_OPTIONS}
          groupingMode={groupingMode} onGroupingModeChange={setGroupingMode} groupingStrategies={GROUPING_STRATEGIES}
          showExcluded={showExcluded} onToggleShowExcluded={() => setShowExcluded((prev) => !prev)}
          viewMode={viewMode} onViewModeChange={setViewMode}
          columnCount={columnCount} onColumnCountChange={setColumnCount} minColumns={MIN_COLUMNS} maxColumns={MAX_COLUMNS}
          bridgeSide={bridgeSide} onBridgeSideChange={setBridgeSide}
        />
      </div>
      <AddCategoryDialog
        open={showAddCategory}
        onOpenChange={setShowAddCategory}
        onAdd={handleAddCategory}
      />

      {/* View Content — auf der Playmat: nur die Spielwelt ist physisch,
          ActionBar/Brücke/Controls bleiben außerhalb. */}
      <div className="de-playmat">
      {viewMode === VIEW_MODES.STACKS && (
        <StacksView
          externalDrop={externalDrop}
          deck={selectedDeck}
          isOwnProfile={isOwnProfile}
          onCategoryChange={handleCategoryChange}
          canEditQuantity={canEditDeck}
          onQuantityDelta={handleQuantityDeltaWithUndo}
          columnCount={columnCount}
          sortBy={sortBy}
          searchTerm={searchTerm}
          highlightedCmc={highlightedCmc}
          strategy={strategy}
          showExcluded={showExcluded}
          matchingCardIds={matchingCardIds}
          isDraggingCategory={!!activeCategoryId}
          onRegisterDndHandlers={handleRegisterDndHandlers}
          onRemoveCardsByCategory={handleRemoveCardsByCategory}
          onShowDetails={handleShowDetails}
          onSwitchPrint={canEditDeck ? handleSwitchPrint : undefined}
          stickyTop={controlsBarHeight}
          highlightedNewCardId={highlightedNewCardId}
          deckLabels={isOwnProfile ? deckLabels : []}
          getCardLabels={getCardLabels}
          onAssignLabel={assignLabelToCard}
          onRemoveLabel={removeLabelFromCard}
          onNewLabelRequest={handleNewLabelRequest}
          onDropError={handleDropError}
          onCategoryRenamed={handleCategoryRenamed}
          onCardMoved={handleCardMoved}
          onCatalogCardDrop={handleCatalogCardDrop}
        />
      )}
      {viewMode === VIEW_MODES.ROWS && (
        <RowsView
          externalDrop={externalDrop}
          deck={selectedDeck}
          isOwnProfile={isOwnProfile}
          onCategoryChange={handleCategoryChange}
          canEditQuantity={canEditDeck}
          onQuantityDelta={handleQuantityDeltaWithUndo}
          columnCount={columnCount}
          sortBy={sortBy}
          searchTerm={searchTerm}
          highlightedCmc={highlightedCmc}
          strategy={strategy}
          showExcluded={showExcluded}
          matchingCardIds={matchingCardIds}
          isDraggingCategory={!!activeCategoryId}
          onRegisterDndHandlers={handleRegisterDndHandlers}
          onRemoveCardsByCategory={handleRemoveCardsByCategory}
          onShowDetails={handleShowDetails}
          onSwitchPrint={canEditDeck ? handleSwitchPrint : undefined}
          stickyTop={controlsBarHeight}
          highlightedNewCardId={highlightedNewCardId}
          deckLabels={isOwnProfile ? deckLabels : []}
          getCardLabels={getCardLabels}
          onAssignLabel={assignLabelToCard}
          onRemoveLabel={removeLabelFromCard}
          onNewLabelRequest={handleNewLabelRequest}
          onDropError={handleDropError}
          onCategoryRenamed={handleCategoryRenamed}
          onCardMoved={handleCardMoved}
          onCatalogCardDrop={handleCatalogCardDrop}
        />
      )}
      {viewMode === VIEW_MODES.TABLE && (
        <TableView
          externalDrop={externalDrop}
          deck={selectedDeck}
          isOwnProfile={isOwnProfile}
          onCategoryChange={handleCategoryChange}
          canEditQuantity={canEditDeck}
          onQuantityDelta={handleQuantityDeltaWithUndo}
          sortBy={sortBy}
          searchTerm={searchTerm}
          highlightedCmc={highlightedCmc}
          strategy={strategy}
          showExcluded={showExcluded}
          matchingCardIds={matchingCardIds}
          isDraggingCategory={!!activeCategoryId}
          onRegisterDndHandlers={handleRegisterDndHandlers}
          onRemoveCardsByCategory={handleRemoveCardsByCategory}
          onShowDetails={handleShowDetails}
          onSwitchPrint={canEditDeck ? handleSwitchPrint : undefined}
          stickyTop={controlsBarHeight}
          highlightedNewCardId={highlightedNewCardId}
          deckLabels={isOwnProfile ? deckLabels : []}
          getCardLabels={getCardLabels}
          onAssignLabel={assignLabelToCard}
          onRemoveLabel={removeLabelFromCard}
          onNewLabelRequest={handleNewLabelRequest}
          onDropError={handleDropError}
          onCategoryRenamed={handleCategoryRenamed}
          onCardMoved={handleCardMoved}
          onCatalogCardDrop={handleCatalogCardDrop}
        />
      )}
      {viewMode === VIEW_MODES.COMPACT && (
        <CompactView
          externalDrop={externalDrop}
          deck={selectedDeck}
          isOwnProfile={isOwnProfile}
          onCategoryChange={handleCategoryChange}
          canEditQuantity={canEditDeck}
          onQuantityDelta={handleQuantityDeltaWithUndo}
          sortBy={sortBy}
          searchTerm={searchTerm}
          highlightedCmc={highlightedCmc}
          strategy={strategy}
          showExcluded={showExcluded}
          matchingCardIds={matchingCardIds}
          isDraggingCategory={!!activeCategoryId}
          onRegisterDndHandlers={handleRegisterDndHandlers}
          onRemoveCardsByCategory={handleRemoveCardsByCategory}
          onShowDetails={handleShowDetails}
          onSwitchPrint={canEditDeck ? handleSwitchPrint : undefined}
          stickyTop={controlsBarHeight}
          highlightedNewCardId={highlightedNewCardId}
          deckLabels={isOwnProfile ? deckLabels : []}
          getCardLabels={getCardLabels}
          onAssignLabel={assignLabelToCard}
          onRemoveLabel={removeLabelFromCard}
          onNewLabelRequest={handleNewLabelRequest}
          onDropError={handleDropError}
          onCategoryRenamed={handleCategoryRenamed}
          onCardMoved={handleCardMoved}
          onCatalogCardDrop={handleCatalogCardDrop}
        />
      )}
      </div>

      {/* Token-Bereich (minHeight als Puffer für Hover-Expand in StacksView) —
          eigener Seiten-Abschnitt außerhalb der Playmat, kein Teil des
          physischen Spielfelds. */}
      <TokenSection
        ref={tokenSectionRef}
        tokens={selectedDeck?.tokens}
        minHeight={450}
        onExport={() => { setExportTokenMode('only'); setShowExportModal(true); }}
      />

      {/* Bei offenem Katalog übernimmt dessen Suchfeld das Hinzufügen, der Dock behält nur den
          Kategorie-Knopf; in Stufe 2 gibt es keine Playmat, zu der eine Kategorie gehören könnte. */}
      {canEditDeck && !catalogStageTwo && (
        <FloatingAddDock
          addCardInputRef={inputRef}
          addCardSearchTerm={addCardSearchTerm}
          onAddCardSearchChange={handleAddCardSearchChange}
          onAddCardKeyDown={handleKeyDown}
          onAddCardBlur={handleAddCardBlur}
          showAutocomplete={showAutocomplete}
          autocompleteResults={autocompleteResults}
          highlightedIndex={highlightedIndex}
          onAutocompleteSelect={handleAutocompleteSelect}
          onOpenAddCategory={() => setShowAddCategory(true)}
          canAddCategory={Boolean(strategy?.editable)}
          onOpenCatalogSearch={() => openCatalog('search')}
          searchHidden={catalogOpen}
          rightInset={dockRightInset}
          hidden={externalDrop.active}
        />
      )}

      {externalDrop.active && (
        <ExternalDropBar
          highlighted={externalDrop.target === EXTERNAL_DROP_AUTO}
          dropProps={externalDrop.propsFor(EXTERNAL_DROP_AUTO, null)}
        />
      )}
    </div>
      
    {/* DragOverlay — die Karte fliegt smooth zur Drop-Target-Position.
        Für alle Views einheitliches Karten-Bild als Drag-Vorschau. In Table/Compact
        nutzen wir eine kleinere Breite (140px) damit das Overlay nicht zu sperrig wirkt. */}
    <DragOverlay dropAnimation={dropAnimation}>
      {(activeCard || activeCatalogEntry) ? (() => {
        const gezogen = activeCard || activeCatalogEntry;
        const isCompactDragView = viewMode === VIEW_MODES.TABLE || viewMode === VIEW_MODES.COMPACT;
        const previewWidth = isCompactDragView ? 140 : (viewDndHandlers.current.cardWidth || 200);
        return (
          <div style={{ width: previewWidth, cursor: 'grabbing', opacity: 0.9 }}>
            <UiCardImage src={gezogen.imageUrl} backSrc={gezogen.imageUrlBack} alt={gezogen.name} />
          </div>
        );
      })() : activeCategoryId ? (
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: 'var(--accent)',
            borderRadius: '6px',
            color: 'var(--text-on-accent)',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            boxShadow: '0 4px 12px var(--shadow-md)',
            cursor: 'grabbing',
          }}
        >
          {formatCategoryName(activeCategoryId.replace('cat-', ''))}
        </div>
      ) : null}
    </DragOverlay>
    </DndContext>
    </div>
    </Page>

    {/* Export Modal */}
    <DeckExportModal
      deck={selectedDeck}
      isOpen={showExportModal}
      onClose={() => setShowExportModal(false)}
      excludedCategoryNames={excludedCategoryNames}
      initialTokenMode={exportTokenMode}
      isOwnProfile={isOwnProfile}
    />

    <CardDetail
      open={!!detail}
      onOpenChange={(next) => { if (!next) setDetail(null); }}
      oracleId={detail?.oracleId}
      scryfallId={detail?.scryfallId}
      fallback={detail?.fallback ?? null}
      section={detail?.section}
      context="switch-print"
      // Über die Brücken-Kachel öffnet das Modal auch Karten, die noch gar nicht im Deck
      // liegen — für die gibt es keine Deck-Karte, deren Print sich wechseln ließe.
      canAct={canEditDeck && !!detailCard}
      printLocked={!!detailCard?.printLocked}
      onSwitchPrint={async (newScryfallId) => {
        const ok = await cardActions.replaceCardPrint(detail.scryfallId, newScryfallId);
        if (ok) {
          pushToast({ tone: 'success', message: 'Print gewechselt' });
          setDetail((cur) => (cur ? { ...cur, scryfallId: newScryfallId } : cur));
        }
        return ok;
      }}
      onUnlockPrint={handleUnlockPrint}
    />

    {newLabelModal && (
      <NewLabelModal
        onConfirm={handleNewLabelConfirm}
        onClose={() => setNewLabelModal(null)}
      />
    )}
    </>
  );
}
